/**
 * Tarjeta de producto de las grillas.
 *
 * Es un `<article>` porque cada tarjeta se entiende sola fuera de su lista. Todo
 * el bloque navegable es un `<a>` real hacia la ficha —la foto y el nombre
 * apuntan al mismo destino, con el nombre del producto como texto del enlace—,
 * que es lo que un robot puede seguir y lo que permite abrirlo en otra pestana.
 */
import { Link } from 'react-router-dom';

import type { ProductoResumen } from '@bandu/compartido';

import { urlAbsoluta } from '../../seo/urls';
import { porcentajeDescuento } from '../../utilidades/formato';
import { rutas } from '../../utilidades/rutas';
import { whatsappProducto } from '../../utilidades/whatsapp';
import { registrarClicWhatsapp } from '../../servicios/metricas.servicio';
import { BotonAgregar } from '../carro/BotonAgregar';
import { Icono } from '../comunes/Icono';
import { EtiquetaStock } from '../comunes/EtiquetaStock';
import { FotoProducto } from '../comunes/Imagen';
import { Precio } from '../comunes/Precio';

export interface TarjetaProductoProps {
  producto: ProductoResumen;
  /** La primera fila de la portada se carga sin diferir: es el LCP. */
  prioridad?: boolean;
}

export function TarjetaProducto({ producto, prioridad = false }: TarjetaProductoProps) {
  const descuento = porcentajeDescuento(producto.precio, producto.precioAnterior);
  const agotado = producto.estadoStock === 'agotado';
  const enlace = rutas.producto(producto.slug);

  return (
    <article className={agotado ? 'tarjeta agotado' : 'tarjeta'}>
      <div className="tarjeta-img">
        <p className="tarjeta-etqs">
          {descuento !== null ? <span className="etq etq-oferta">-{descuento}%</span> : null}
          {agotado ? <span className="etq etq-critico">Sin stock</span> : null}
        </p>
        <Link to={enlace} tabIndex={-1} aria-hidden="true">
          <FotoProducto imagen={producto.imagen} nombre={producto.nombre} prioridad={prioridad} />
        </Link>
      </div>

      <div className="tarjeta-cuerpo">
        {producto.marca !== null ? <p className="tarjeta-marca">{producto.marca}</p> : null}

        <h3>
          <Link className="tarjeta-n" to={enlace}>
            {producto.nombre}
          </Link>
        </h3>

        {/* La tarjeta de un producto con tallas representa a todas: el codigo de
            una sola confundiria. */}
        <p className="tarjeta-sku">
          {producto.tieneVariantes ? 'Varias opciones' : producto.sku}
        </p>
        <Precio precio={producto.precio} precioAnterior={producto.precioAnterior} />
        <p className="tarjeta-stock">
          <EtiquetaStock stock={producto.stock} />
        </p>

        {/* Un producto agotado no se puede agregar: deriva a WhatsApp, que es
            donde hoy se resuelve una reposicion (WSP-01). */}
        <p className="tarjeta-acciones">
          {agotado ? (
            <a
              className="btn btn-sm btn-wa"
              href={whatsappProducto(
                producto.nombre,
                producto.sku,
                urlAbsoluta(enlace),
              )}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => registrarClicWhatsapp({ origen: 'ficha', productoSku: producto.sku })}
            >
              <Icono nombre="wa" />
              Consultar
            </a>
          ) : producto.tieneVariantes ? (
            // Hay que elegir talla o color antes de agregar: eso pasa en la ficha.
            <Link className="btn btn-sm btn-primario" to={enlace}>
              Ver opciones
            </Link>
          ) : (
            <BotonAgregar producto={producto} tamano="sm" />
          )}
        </p>
      </div>
    </article>
  );
}
